import { MultiSelectDropdown } from "@/components/MultiSelectDropdown"

import type { DashboardTab } from "../state/dashboardReducer"

interface BuyerOption {
  id: number
  label: string
}

interface DashboardFilterDropdownsProps {
  activeTab: DashboardTab
  selectedNetworks: string[]
  onNetworksChange: (values: string[]) => void
  selectedBuyerStatuses: string[]
  onBuyerStatusesChange: (values: string[]) => void
  selectedAccountStatuses: string[]
  onAccountStatusesChange: (values: string[]) => void
  selectedBuyers: number[]
  onBuyersChange: (ids: number[]) => void
  buyerOptions: BuyerOption[]
  layout?: "inline" | "stacked"
}

const BUYER_STATUS_OPTIONS = [
  { id: "ENABLE", label: "Active" },
  { id: "DISABLE", label: "Paused" },
  { id: "DELETE", label: "Deleted" },
] as const

const ACCOUNT_STATUS_OPTIONS = [
  { id: "WITH_SPEND", label: "Active (with spend)" },
  { id: "WITHOUT_SPEND", label: "Not active (no spend)" },
  { id: "DELETED", label: "Deleted" },
] as const

export function DashboardFilterDropdowns({
  activeTab,
  selectedNetworks,
  onNetworksChange,
  selectedBuyerStatuses,
  onBuyerStatusesChange,
  selectedAccountStatuses,
  onAccountStatusesChange,
  selectedBuyers,
  onBuyersChange,
  buyerOptions,
  layout = "inline",
}: DashboardFilterDropdownsProps) {
  const isStacked = layout === "stacked"
  const controlWidth = isStacked ? "100%" : undefined
  const isAccountTab = activeTab === "account"

  return (
    <div
      className={
        isStacked
          ? "flex w-full flex-col gap-3"
          : "inline-flex min-w-max shrink-0 flex-nowrap items-center gap-2"
      }
    >
      <div className={isStacked ? "space-y-1" : undefined}>
        {isStacked && (
          <span className="text-[11px] font-semibold uppercase tracking-wider text-[#b0b0b0]">
            Networks
          </span>
        )}
        <MultiSelectDropdown
          label="All Networks"
          options={[
            { id: "roots", label: "Roots" },
            { id: "klow", label: "KLOW" },
            { id: "adw", label: "ADW" },
            { id: "unassigned", label: "Unassigned" },
          ]}
          selectedIds={selectedNetworks}
          onChange={onNetworksChange}
          className={isStacked ? "w-full" : "shrink-0"}
          minWidth={controlWidth ?? "160px"}
        />
      </div>

      <div className={isStacked ? "space-y-1" : undefined}>
        {isStacked && (
          <span className="text-[11px] font-semibold uppercase tracking-wider text-[#b0b0b0]">
            Status
          </span>
        )}
        {isAccountTab ? (
          <MultiSelectDropdown
            label="All Status"
            options={[...ACCOUNT_STATUS_OPTIONS]}
            selectedIds={selectedAccountStatuses}
            onChange={onAccountStatusesChange}
            className={isStacked ? "w-full" : "shrink-0"}
            minWidth={controlWidth ?? "190px"}
          />
        ) : (
          <MultiSelectDropdown
            label="All Status"
            options={[...BUYER_STATUS_OPTIONS]}
            selectedIds={selectedBuyerStatuses}
            onChange={onBuyerStatusesChange}
            className={isStacked ? "w-full" : "shrink-0"}
            minWidth={controlWidth ?? "150px"}
          />
        )}
      </div>

      <div className={isStacked ? "space-y-1" : undefined}>
        {isStacked && (
          <span className="text-[11px] font-semibold uppercase tracking-wider text-[#b0b0b0]">
            Buyers
          </span>
        )}
        <MultiSelectDropdown
          label="All Buyers"
          options={buyerOptions}
          selectedIds={selectedBuyers}
          onChange={onBuyersChange}
          className={isStacked ? "w-full" : "shrink-0"}
          minWidth={controlWidth ?? "160px"}
          searchable
          searchPlaceholder="Search buyers..."
          showApplyClearActions
        />
      </div>
    </div>
  )
}
